import { AppThunk } from "@Store/index";
import fetchInventory from "@Pages/PropertyPage/data/fetchInventory";
import fetchReviewMedia from "@Pages/PropertyPage/data/fetchReviewMedia";
import { getMetaSuccess } from "@App/appReducer";
import customWindowLocation from "@Utils/customWindowLocation";
import fetchInventoryRooms from "@Pages/PropertyPage/data/fetchInventoryRooms";
import { sortVideosWithLiveTourFirst } from "@Pages/PropertyPage/components/SectionFeatured.tsx/utils";
import { getVideoPageRequesting, getVideoPageSuccess, getVideoPageError } from "./videoPageReducer";

const getVideoPageData =
  ({ id, videoId, staticContext }: any): AppThunk =>
  async (dispatch, getState) => {
    dispatch(getVideoPageRequesting());
    if (!id) {
      console.error("VideoPage: Missing property ID");
      return dispatch(getVideoPageError("Missing property ID"));
    }

    try {
      const { appData } = getState();
      const selectedLang = appData.userLanguage || "en";
      const userCurrency = appData.userCurrency || "";

      const [invRes, reviewData, roomTypesRes]: any = await Promise.all([
        fetchInventory(
          {
            id,
            regionId: "",
            token: "",
            selectedLang,
            userCurrency,
            useJavaApi: true,
          },
          {
            methods: "children_short,reviews_count,reviews_rating,region,meta_facts",
          },
        ),
        fetchReviewMedia({ id, selectedLang }).catch(() => null),
        fetchInventoryRooms({ id, selectedLang }).catch(() => null),
      ]);

      const inventoryData = invRes?.data?.data;

      if (!inventoryData) {
        if (staticContext) {
          staticContext.url = "/";
        }
        if (__CLIENT__) customWindowLocation({ url: "/" });
        return null;
      }

      // Deleted inventories - redirect to search or home
      if (inventoryData.status === "deleted") {
        const redirectUrl = inventoryData.region?.canonical_name
          ? `/places/search/${inventoryData.region.canonical_name}`
          : "/";
        if (staticContext) {
          staticContext.url = redirectUrl;
        }
        if (__CLIENT__) customWindowLocation({ url: redirectUrl });
        return null;
      }

      // Check if the canonical URL is valid
      if (id !== inventoryData.canonical_name) {
        const redirectUrl = `/places/${inventoryData.canonical_name}/video/${videoId || ""}`;
        if (staticContext) {
          staticContext.url = redirectUrl;
        }
        if (__CLIENT__) customWindowLocation({ url: redirectUrl });
        return null;
      }

      // Collect ALL videos from property and children_short
      // Tag each video with its associated room type for display
      const videos: any[] = [];
      if (inventoryData.videos?.length > 0) {
        videos.push(...inventoryData.videos.map((v: any) => ({ ...v, associatedRoomType: null })));
      }
      const roomTypesData = roomTypesRes?.data?.data?.result || [];

      inventoryData.children_short?.forEach((child: any) => {
        if (child.videos?.length > 0) {
          const matchedRoomType = roomTypesData.find((rt: any) => rt.id === child.id);
          videos.push(
            ...child.videos.map((v: any) => ({
              ...v,
              associatedRoomType: {
                name: child.name,
                id: child.id,
                meta: matchedRoomType?.meta || child.meta,
                images: child.images,
                pricing: child.pricing,
                features: matchedRoomType?.features || child.features,
              },
            })),
          );
        }
      });

      // Dispatch meta tags for social sharing / SEO
      const meta: any = {};
      meta.title_prefix = `${inventoryData.name} - Video Tour`;
      meta.description = `Watch video tours of ${inventoryData.name}. Explore rooms, amenities, and surroundings before booking.`;
      meta.canonical_url_suffix = `/places/${inventoryData.canonical_name}/video/${videoId || ""}`;
      if (inventoryData?.images?.[0]?.base_path) {
        meta.ogImage = inventoryData.images[0].base_path;
      }
      meta.robots = "noindex,nofollow";
      dispatch(getMetaSuccess(meta));

      // Only store the fields needed by the video page to reduce SSR payload
      const videoPageData = {
        id: inventoryData.id,
        canonical_name: inventoryData.canonical_name,
        name: inventoryData.name,
        reviews_rating: inventoryData.reviews_rating,
        reviews_count: inventoryData.reviews_count,
        images: inventoryData.images?.slice(0, 1) || [],
        // read by the page's full-screen carousel (P1D-5542)
        is_watermark_disabled: inventoryData.meta?.is_watermark_disabled === true,
      };

      dispatch(
        getVideoPageSuccess({
          data: videoPageData,
          reviewData: [
            ...(reviewData?.data?.data?.result || []),
            ...(reviewData?.data?.data?.resultWithoutMedia || []),
          ],
          videos: sortVideosWithLiveTourFirst(videos),
          selectedVideoId: videoId || null,
        }),
      );

      return null;
    } catch (err) {
      console.error(err);
      return dispatch(getVideoPageError(err instanceof Error ? err.message : String(err)));
    }
  };

export default getVideoPageData;
